/**
 * Sorting and per-column filtering for the dashboard tables (QA, 9 Oct 2026:
 * "add filter and sort function to all tables"). Pure functions — the hook in
 * `useTableControls.js` holds the state, `SortFilterHeaderCell` draws the header.
 *
 * A column is described by
 *   {key, label, type: 'text' | 'band' | 'number' | 'percent' | 'date',
 *    accessor?: (row) => value, sortable?: true, filterable?: true, order?: string[]}
 *
 * Rows are whatever the page already renders. Rows made by `rowsFrom` carry
 * `_raw` (the value as sent) and `_suppressed` (per column); both are honoured:
 * a suppressed cell has no value and always sorts last, in either direction.
 */

const NUMERIC = new Set(['number', 'percent']);

export const isEmpty = (v) => v === null || v === undefined || v === '' || v === '—' || v === '-';

/** The value a column holds for a row: the accessor's result, else the raw value, else the shown text; null when there is none. */
export const columnValue = (row, col) => {
    if (!row || !col) return null;
    if (row._suppressed && row._suppressed[col.key]) return null;
    let v;
    if (col.accessor) v = col.accessor(row);
    else if (row._raw && col.key in row._raw && !isEmpty(row._raw[col.key])) v = row._raw[col.key];
    else v = row[col.key];
    return isEmpty(v) ? null : v;
};

/** The text a text/band column shows for a row (what the filter checklist offers); null when empty. */
export const displayText = (row, col) => {
    if (!row || !col) return null;
    if (row._suppressed && row._suppressed[col.key]) return null;
    const v = col.accessor ? col.accessor(row) : row[col.key];
    if (isEmpty(v)) return null;
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') return null;
    return String(v);
};

/** The key a column sorts by: a number for number/percent/date columns, a string otherwise; null when absent. */
export const sortKey = (row, col) => {
    const v = columnValue(row, col);
    if (v === null) return null;
    if (NUMERIC.has(col.type)) {
        const n = typeof v === 'string' ? Number(v.replace(/%\s*$/, '').replace(/,/g, '').trim()) : Number(v);
        return Number.isFinite(n) ? n : null;
    }
    if (col.type === 'date') {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : t;
    }
    return String(v);
};

/** The next sort state after a click on `key`: none → ascending → descending → none. */
export const nextSort = (sort, key) => {
    if (!sort || sort.key !== key) return {key, dir: 'asc'};
    return sort.dir === 'asc' ? {key, dir: 'desc'} : null;
};

const compareKeys = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, {numeric: true, sensitivity: 'base'});
};

/** Rows in sort order; stable; rows without a value last whichever the direction. The same row objects come back. */
export const sortRows = (rows, sort, columns) => {
    if (!sort) return rows;
    const col = (columns || []).find((c) => c.key === sort.key);
    if (!col) return rows;
    const mul = sort.dir === 'desc' ? -1 : 1;
    return rows
        .map((row, i) => ({row, i, k: sortKey(row, col)}))
        .sort((a, b) => {
            if (a.k === null && b.k === null) return a.i - b.i;
            if (a.k === null) return 1;
            if (b.k === null) return -1;
            return (compareKeys(a.k, b.k) * mul) || (a.i - b.i);
        })
        .map((d) => d.row);
};

const hasBound = (x) => x !== undefined && x !== null && x !== '' && Number.isFinite(Number(x));
/** True when a filter value actually filters: a non-empty checklist or at least one bound. */
export const isActiveFilter = (f) => !!f && ((f.values instanceof Set && f.values.size > 0) || hasBound(f.min) || hasBound(f.max));

/**
 * Rows that pass every active filter. A checklist filter keeps the rows whose
 * shown text is ticked (so a row with "—" drops out once anything is ticked, as
 * the old drop-downs did); a range filter keeps rows whose number lies within
 * the bounds and drops rows without a number.
 */
export const applyFilters = (rows, filters, columns) => {
    const active = Object.entries(filters || {}).filter(([, f]) => isActiveFilter(f));
    if (!active.length) return rows;
    return rows.filter((row) => active.every(([key, f]) => {
        const col = (columns || []).find((c) => c.key === key);
        if (!col) return true;
        if (f.values instanceof Set) {
            const t = displayText(row, col);
            return t !== null && f.values.has(t);
        }
        const n = sortKey(row, col);
        if (n === null) return false;
        if (hasBound(f.min) && n < Number(f.min)) return false;
        if (hasBound(f.max) && n > Number(f.max)) return false;
        return true;
    }));
};

/** Distinct shown values of a text/band column, in the column's `order` if given, else alphabetical (numeric-aware). */
export const optionsFor = (rows, col) => {
    if (!col) return [];
    const vals = [...new Set((rows || []).map((r) => displayText(r, col)).filter((v) => v !== null))];
    if (col.order) {
        const pos = (v) => { const i = col.order.indexOf(v); return i < 0 ? col.order.length : i; };
        return vals.sort((a, b) => (pos(a) - pos(b)) || compareKeys(a, b));
    }
    return vals.sort(compareKeys);
};

/** {min, max} of a numeric column over the rows, or null when no row has a number. */
export const rangeFor = (rows, col) => {
    if (!col) return null;
    const nums = (rows || []).map((r) => sortKey(r, col)).filter((n) => typeof n === 'number');
    return nums.length ? {min: Math.min(...nums), max: Math.max(...nums)} : null;
};

export const isSortable = (col) => !!col && col.sortable !== false;

/**
 * Whether a column gets a filter control: opted in (default), enough rows to
 * be worth filtering, and something to choose between. Date columns sort only.
 */
export const isFilterable = (col, rows, minRows = 5) => {
    if (!col || col.filterable === false || col.type === 'date') return false;
    if ((rows || []).length < minRows) return false;
    if (NUMERIC.has(col.type)) return rangeFor(rows, col) !== null;
    return optionsFor(rows, col).length >= 2;
};

/** "Region: Abu Dhabi, Dubai +1" / "Score: 40 – 80" — one chip per active filter, for the toolbar. */
export const activeFilterChips = (filters, columns) => Object.entries(filters || {})
    .filter(([, f]) => isActiveFilter(f))
    .map(([key, f]) => {
        const col = (columns || []).find((c) => c.key === key);
        const label = col?.label || key;
        if (f.values instanceof Set) {
            const vals = [...f.values];
            const shown = vals.slice(0, 2).join(', ') + (vals.length > 2 ? ` +${vals.length - 2}` : '');
            return {key, label: `${label}: ${shown}`};
        }
        const unit = col?.type === 'percent' ? '%' : '';
        const lo = hasBound(f.min) ? `${f.min}${unit}` : '';
        const hi = hasBound(f.max) ? `${f.max}${unit}` : '';
        const text = lo && hi ? `${lo} – ${hi}` : (lo ? `≥ ${lo}` : `≤ ${hi}`);
        return {key, label: `${label}: ${text}`};
    });
